import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CircleHelp, Loader2, MessageSquareText, RefreshCw } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@akuna/ui";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useProfile } from "@/hooks/useProfile";
import { useClinicMetaConnection } from "@/hooks/useClinicMetaConnection";
import {
  useClinicWhatsAppCloudConfig,
  useUpsertClinicWhatsAppCloudConfig,
} from "@/hooks/useClinicWhatsAppCloudConfig";
import {
  useClinicWhatsAppWebhookSubscription,
  useUpsertClinicWhatsAppWebhookSubscription,
} from "@/hooks/useClinicWhatsAppWebhookSubscription";
import {
  useClinicWhatsAppApplicationReminderRule,
  useNextWhatsAppApplicationReminderTrigger,
  useUpsertClinicWhatsAppApplicationReminderRule,
} from "@/hooks/useClinicWhatsAppApplicationReminderRule";
import { supabase } from "@/integrations/supabase/client";
import { loadFacebookSdk } from "@/lib/facebookSdk";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  APPLICATION_REMINDER_TEMPLATE_DEFAULT_BODY,
  APPLICATION_REMINDER_TOKEN_DEFINITIONS,
  buildApplicationReminderMetaTemplate,
  findUnknownApplicationReminderTokens,
  renderApplicationReminderPreview,
  type ApplicationReminderTokenCategory,
  type ApplicationReminderTokenDefinition,
  type ApplicationReminderTokenKey,
} from "@/lib/whatsapp/application-reminder-template";

const FIXED_WEBHOOK_VERIFY_TOKEN_SECRET = "WHATSAPP_WEBHOOK_VERIFY_TOKEN";
const FIXED_WEBHOOK_APP_SECRET = "META_WHATSAPP_APP_SECRET";
const META_APP_ID = import.meta.env.VITE_META_APP_ID as string | undefined;
const META_CONFIG_ID = import.meta.env.VITE_META_CONFIG_ID as string | undefined;
const API_VERSION = "v25.0";
const TEMPLATE_LANGUAGE = "pt_BR";

type MetaTemplate = {
  name: string;
  language?: string;
  status?: string;
  category?: string;
  components?: Array<{
    type?: string;
    text?: string;
    format?: string;
    buttons?: Array<{
      type?: string;
      text?: string;
      url?: string;
      phone_number?: string;
      example?: unknown;
    }>;
  }>;
};

type EmbeddedSignupMetadata = {
  wabaId: string | null;
  metaBusinessId: string | null;
  phoneNumberId: string | null;
};

type LiveMetaPhoneNumber = {
  id?: string;
  display_phone_number?: string;
  verified_name?: string;
  code_verification_status?: string;
  quality_rating?: string;
};

const DEFAULT_MAPPING: Record<string, string> = {
  "1": "owner.name",
  "2": "pet.name",
  "3": "applications.name",
  "4": "applications.next_dose_date",
};

const extractTemplatePlaceholders = (value: unknown): string[] => {
  if (typeof value === "string") {
    return value.match(/\{\{\w+\}\}/g) ?? [];
  }
  if (Array.isArray(value)) {
    return value.flatMap((item) => extractTemplatePlaceholders(item));
  }
  if (value && typeof value === "object") {
    return Object.values(value as Record<string, unknown>).flatMap((item) =>
      extractTemplatePlaceholders(item)
    );
  }
  return [];
};

const countUniquePlaceholderSlots = (text: string) =>
  new Set((extractTemplatePlaceholders(text) ?? []).map((item) => item.replace(/[^\d]/g, ""))).size;

const readNestedString = (value: unknown, paths: string[][]): string | null => {
  for (const path of paths) {
    let current: unknown = value;
    for (const key of path) {
      if (!current || typeof current !== "object" || !(key in (current as Record<string, unknown>))) {
        current = null;
        break;
      }
      current = (current as Record<string, unknown>)[key];
    }
    if (typeof current === "string" && current.trim().length > 0) {
      return current.trim();
    }
  }
  return null;
};

const isFinishEmbeddedSignupEvent = (payload: unknown): boolean => {
  if (!payload || typeof payload !== "object") return false;
  const event = String((payload as Record<string, unknown>).event ?? (payload as Record<string, unknown>).type ?? "");
  return event.toUpperCase() === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING";
};

const hasFinishEmbeddedSignupMetadata = (payload: unknown): payload is Record<string, unknown> => {
  return (
    isFinishEmbeddedSignupEvent(payload) &&
    !!extractEmbeddedSignupMetadata(payload)?.wabaId &&
    !!extractEmbeddedSignupMetadata(payload)?.metaBusinessId &&
    !!extractEmbeddedSignupMetadata(payload)?.phoneNumberId
  );
};

const extractEmbeddedSignupMetadata = (payload: unknown): EmbeddedSignupMetadata => {
  const wabaId = readNestedString(payload, [
    ["waba_id"],
    ["wabaId"],
    ["data", "waba_id"],
    ["data", "wabaId"],
    ["payload", "waba_id"],
    ["payload", "wabaId"],
  ]);
  const metaBusinessId = readNestedString(payload, [
    ["business_id"],
    ["businessId"],
    ["meta_business_id"],
    ["metaBusinessId"],
    ["data", "business_id"],
    ["data", "businessId"],
    ["data", "meta_business_id"],
    ["data", "metaBusinessId"],
    ["payload", "business_id"],
    ["payload", "businessId"],
  ]);
  const phoneNumberId = readNestedString(payload, [
    ["phone_number_id"],
    ["phoneNumberId"],
    ["data", "phone_number_id"],
    ["data", "phoneNumberId"],
    ["payload", "phone_number_id"],
    ["payload", "phoneNumberId"],
  ]);

  return { wabaId, metaBusinessId, phoneNumberId };
};

export function WhatsAppMetaSettingsTab() {
  const { t } = useTranslation("settings");
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;
  const { data: metaConnection, refetch: refetchMetaConnection } = useClinicMetaConnection(clinicId);
  const { data: cloudConfig, isLoading, refetch: refetchCloudConfig } = useClinicWhatsAppCloudConfig(clinicId);
  const upsertCloudConfig = useUpsertClinicWhatsAppCloudConfig();
  const { data: webhookSub } = useClinicWhatsAppWebhookSubscription(clinicId);
  const upsertWebhookSub = useUpsertClinicWhatsAppWebhookSubscription();
  const { data: applicationReminderRule } = useClinicWhatsAppApplicationReminderRule(clinicId);
  const { data: nextReminderTrigger, isLoading: isLoadingNextReminderTrigger } =
    useNextWhatsAppApplicationReminderTrigger(clinicId);
  const upsertApplicationReminderRule = useUpsertClinicWhatsAppApplicationReminderRule();

  const isEnabled = true;
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [wabaId, setWabaId] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [templateLanguage, setTemplateLanguage] = useState("pt_BR");
  const [newTemplateLanguage, setNewTemplateLanguage] = useState("pt_BR");
  const [templateVariableMapping, setTemplateVariableMapping] = useState<Record<string, string>>(DEFAULT_MAPPING);

  const [webhookIsActive, setWebhookIsActive] = useState(true);

  const [testPhone, setTestPhone] = useState("");
  const [isSendingTest, setIsSendingTest] = useState(false);

  const [isLoadingNumbers, setIsLoadingNumbers] = useState(false);
  const [isSelectingNumber, setIsSelectingNumber] = useState<string | null>(null);
  const [livePhoneNumbers, setLivePhoneNumbers] = useState<LiveMetaPhoneNumber[]>([]);
  const [isMetaSdkReady, setIsMetaSdkReady] = useState(false);
  const [isEmbeddedSignupLoading, setIsEmbeddedSignupLoading] = useState(false);
  const [embeddedSignupPayload, setEmbeddedSignupPayload] = useState<Record<string, unknown> | null>(null);
  const [embeddedSignupMetadata, setEmbeddedSignupMetadata] = useState<EmbeddedSignupMetadata>({
    wabaId: null,
    metaBusinessId: null,
    phoneNumberId: null,
  });
  const embeddedSignupPayloadRef = useRef<Record<string, unknown> | null>(null);
  const embeddedSignupMetadataRef = useRef<EmbeddedSignupMetadata>({
    wabaId: null,
    metaBusinessId: null,
    phoneNumberId: null,
  });
  const [reminderIsActive, setReminderIsActive] = useState(false);
  const [reminderOffsetUnit, setReminderOffsetUnit] = useState<"days" | "weeks" | "months">("weeks");
  const [reminderOffsetValue, setReminderOffsetValue] = useState(1);
  const [reminderSendTime, setReminderSendTime] = useState("09:00");
  const [reminderTimezone, setReminderTimezone] = useState("America/Sao_Paulo");
  const [isDisconnectHelpOpen, setIsDisconnectHelpOpen] = useState(false);

  const [availableTemplates, setAvailableTemplates] = useState<MetaTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [newTemplateCategory, setNewTemplateCategory] = useState<"MARKETING" | "UTILITY" | "AUTHENTICATION">("UTILITY");
  const [newTemplateHeader, setNewTemplateHeader] = useState("");
  const [newTemplateBody, setNewTemplateBody] = useState(APPLICATION_REMINDER_TEMPLATE_DEFAULT_BODY);
  const [newTemplateHeaderExamples, setNewTemplateHeaderExamples] = useState("");
  const [newTemplateFooter, setNewTemplateFooter] = useState("");
  const [newTemplateQuickReplies, setNewTemplateQuickReplies] = useState("");
  const [isTemplateSelectionDirty, setIsTemplateSelectionDirty] = useState(false);

  const [videoChecklist, setVideoChecklist] = useState({
    startedRecording: false,
    showedFlow: false,
    createdName: false,
    createdCategory: false,
    createdContent: false,
    showedApproval: false,
    finishedRecording: false,
  });

  const tokenCategoryLabels = useMemo<Record<ApplicationReminderTokenCategory, string>>(
    () => ({
      guardian: t("whatsappMetaTokenCategoryGuardian"),
      patient: t("whatsappMetaTokenCategoryPatient"),
      application: t("whatsappMetaTokenCategoryApplication"),
      clinic: t("whatsappMetaTokenCategoryClinic"),
    }),
    [t],
  );

  const reminderTokenGroups = useMemo(() => {
    return APPLICATION_REMINDER_TOKEN_DEFINITIONS.reduce(
      (groups, token) => {
        groups[token.category].push(token);
        return groups;
      },
      {
        guardian: [],
        patient: [],
        application: [],
        clinic: [],
      } as Record<ApplicationReminderTokenCategory, ApplicationReminderTokenDefinition[]>,
    );
  }, []);

  const reminderTemplatePreview = useMemo(
    () => renderApplicationReminderPreview(newTemplateBody),
    [newTemplateBody],
  );

  const unknownTemplateTokens = useMemo(
    () => findUnknownApplicationReminderTokens(newTemplateBody),
    [newTemplateBody],
  );

  const reminderMetaTemplate = useMemo(() => {
    if (unknownTemplateTokens.length > 0) return null;
    try {
      return buildApplicationReminderMetaTemplate(newTemplateBody);
    } catch {
      return null;
    }
  }, [newTemplateBody, unknownTemplateTokens]);

  const templateStatusLabel = useCallback(
    (status?: string) => {
      const key = (status || "").toUpperCase();
      if (key === "APPROVED") return t("whatsappMetaStatusApproved");
      if (key === "PENDING") return t("whatsappMetaStatusPending");
      if (key === "REJECTED") return t("whatsappMetaStatusRejected");
      return status || "—";
    },
    [t]
  );

  const normalizeMetaStatus = useCallback((value?: string | null) => {
    return (value || "").trim().toUpperCase();
  }, []);

  const qualityLabel = useCallback(
    (quality?: string | null) => {
      const normalized = normalizeMetaStatus(quality);
      if (normalized === "GREEN") return t("whatsappMetaQualityGreen");
      if (normalized === "YELLOW") return t("whatsappMetaQualityYellow");
      if (normalized === "RED") return t("whatsappMetaQualityRed");
      if (normalized === "UNKNOWN") return t("whatsappMetaQualityUnknown");
      if (!normalized) return t("whatsappMetaQualityUnavailable");
      return normalized;
    },
    [normalizeMetaStatus, t]
  );

  const qualityHelp = useCallback(
    (quality?: string | null) => {
      const normalized = normalizeMetaStatus(quality);
      if (normalized === "GREEN") return t("whatsappMetaQualityGreenHelp");
      if (normalized === "YELLOW") return t("whatsappMetaQualityYellowHelp");
      if (normalized === "RED") return t("whatsappMetaQualityRedHelp");
      return t("whatsappMetaQualityUnknownHelp", {
        quality: normalized || "n/a",
      });
    },
    [normalizeMetaStatus, t]
  );

  useEffect(() => {
    if (!cloudConfig) return;
    setPhoneNumberId(cloudConfig.phone_number_id || metaConnection?.phone_number_id || "");
    setWabaId(cloudConfig.waba_id || "");
    setTemplateName(cloudConfig.template_name || "");
    setTemplateLanguage(cloudConfig.template_language || "pt_BR");
    setTemplateVariableMapping(
      cloudConfig.template_variable_mapping && Object.keys(cloudConfig.template_variable_mapping).length > 0
        ? cloudConfig.template_variable_mapping
        : DEFAULT_MAPPING
    );
  }, [cloudConfig, metaConnection?.phone_number_id]);

  useEffect(() => {
    if (!clinicId || !META_APP_ID) return;

    loadFacebookSdk(META_APP_ID, API_VERSION)
      .then(() => setIsMetaSdkReady(true))
      .catch((error) => {
        console.error("Failed to load Facebook SDK", error);
        setIsMetaSdkReady(false);
      });
  }, [clinicId]);

  useEffect(() => {
    if (!metaConnection?.waba_id) return;
    setWabaId((current) => current || metaConnection.waba_id || "");
  }, [metaConnection?.waba_id]);

  useEffect(() => {
    if (!metaConnection?.phone_number_id) return;
    setPhoneNumberId((current) => current || metaConnection.phone_number_id || "");
  }, [metaConnection?.phone_number_id]);

  useEffect(() => {
    if (!webhookSub) return;
    setWebhookIsActive(webhookSub.is_active !== false);
  }, [webhookSub]);

  useEffect(() => {
    if (!applicationReminderRule) return;
    setReminderIsActive(applicationReminderRule.is_active === true);
    setReminderOffsetUnit(applicationReminderRule.offset_unit);
    setReminderOffsetValue(applicationReminderRule.offset_value);
    setReminderSendTime((applicationReminderRule.send_time ?? "09:00:00").slice(0, 5));
    setReminderTimezone(applicationReminderRule.timezone || "America/Sao_Paulo");
  }, [applicationReminderRule]);

  useEffect(() => {
    embeddedSignupPayloadRef.current = embeddedSignupPayload;
  }, [embeddedSignupPayload]);

  useEffect(() => {
    embeddedSignupMetadataRef.current = embeddedSignupMetadata;
  }, [embeddedSignupMetadata]);

  useEffect(() => {
    const handleEmbeddedSignupMessage = (event: MessageEvent) => {
      try {
        const payload =
          typeof event.data === "string" ? JSON.parse(event.data) as Record<string, unknown> : event.data;

        if (!payload || typeof payload !== "object") return;

        if (!isFinishEmbeddedSignupEvent(payload)) return;

        const metadata = extractEmbeddedSignupMetadata(payload);
        setEmbeddedSignupPayload(payload);
        setEmbeddedSignupMetadata(metadata);
        embeddedSignupPayloadRef.current = payload;
        embeddedSignupMetadataRef.current = metadata;

        if (metadata.wabaId) {
          setWabaId(metadata.wabaId);
        }
        if (metadata.phoneNumberId) {
          setPhoneNumberId(metadata.phoneNumberId);
        }
      } catch {
        // Ignore unrelated postMessage payloads.
      }
    };

    window.addEventListener("message", handleEmbeddedSignupMessage);
    return () => window.removeEventListener("message", handleEmbeddedSignupMessage);
  }, []);

  const refreshMetaState = useCallback(async (resolvedWabaId?: string | null) => {
    await Promise.all([
      refetchMetaConnection(),
      refetchCloudConfig(),
    ]);

    const resolvedWaba = resolvedWabaId?.trim() || metaConnection?.waba_id || wabaId.trim();
    if (!resolvedWaba) return;
    setWabaId(resolvedWaba);
  }, [
    wabaId,
    clinicId,
    metaConnection?.waba_id,
    refetchCloudConfig,
    refetchMetaConnection,
    t,
  ]);

  const startEmbeddedSignup = useCallback(async () => {
    if (!clinicId) {
      toast.error(t("whatsappMetaSelectClinicToSaveConfig"));
      return;
    }

    if (!META_APP_ID || !META_CONFIG_ID) {
      toast.error("Missing VITE_META_APP_ID or VITE_META_CONFIG_ID");
      return;
    }

    setIsEmbeddedSignupLoading(true);
    try {
      await loadFacebookSdk(META_APP_ID, API_VERSION);
      setIsMetaSdkReady(true);

      await new Promise<void>((resolve, reject) => {
        window.FB?.login(
          (response: {
            authResponse?: { code?: string };
            status?: string;
          }) => {
            void (async () => {
              try {
                const code = response?.authResponse?.code;
                if (!code) {
                  reject(new Error("Meta signup was cancelled or did not return a code"));
                  return;
                }

                const latestEmbeddedSignupPayload = embeddedSignupPayloadRef.current;
                const latestEmbeddedSignupMetadata = embeddedSignupMetadataRef.current;

                if (!latestEmbeddedSignupPayload || !hasFinishEmbeddedSignupMetadata(latestEmbeddedSignupPayload)) {
                  reject(new Error("Meta signup did not emit FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING yet."));
                  return;
                }

                const finishSignupPayload = {
                  ...latestEmbeddedSignupPayload,
                  event: "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING",
                };

                const { data, error } = await supabase.functions.invoke("meta-embedded-signup-callback", {
                  body: {
                    clinicId,
                    code,
                    wabaId: latestEmbeddedSignupMetadata.wabaId,
                    metaBusinessId: latestEmbeddedSignupMetadata.metaBusinessId,
                    phoneNumberId: latestEmbeddedSignupMetadata.phoneNumberId,
                    embeddedSignupPayload: finishSignupPayload,
                  },
                });
                const result = data as {
                  success?: boolean;
                  error?: string;
                  wabaId?: string | null;
                  phoneNumberId?: string | null;
                  finishSync?: {
                    smbAppStateSync?: boolean;
                    history?: boolean;
                    subscribedApps?: boolean;
                    smbAppStateSyncError?: string | null;
                    historyError?: string | null;
                    subscribedAppsError?: string | null;
                  };
                } | null;

                if (error || !result?.success) {
                  reject(new Error(error?.message || result?.error || "Failed to exchange Meta code"));
                  return;
                }

                const payloadWabaId = result?.wabaId ?? latestEmbeddedSignupMetadata.wabaId;
                const resolvedWabaId =
                  typeof payloadWabaId === "string" && payloadWabaId.trim().length > 0
                    ? payloadWabaId
                    : wabaId.trim() || null;

                if (result?.phoneNumberId) {
                  setPhoneNumberId(result.phoneNumberId);
                }

                await refreshMetaState(resolvedWabaId);
                if (result?.finishSync?.smbAppStateSync || result?.finishSync?.history || result?.finishSync?.subscribedApps) {
                  await loadPhoneNumbers({ silent: false, skipConnectionCheck: true });
                }
                toast.success(t("whatsappMetaConnectSuccess"));
                resolve();
              } catch (callbackError) {
                reject(callbackError);
              }
            })();
          },
          {
            config_id: META_CONFIG_ID,
            response_type: "code",
            override_default_response_type: true,
            extras: {
              setup: {
                business: {
                  name: "Akuna Pet"
                }
              },
              featureType: "whatsapp_business_app_onboarding",
              sessionInfoVersion: 3,
            },
          }
        );
      });
    } catch (error) {
      console.error("Embedded signup failed", error);
      toast.error(error instanceof Error ? error.message : t("whatsappMetaConnectError"));
    } finally {
      setIsEmbeddedSignupLoading(false);
    }
  }, [wabaId, clinicId, refreshMetaState, t]);

  const isMetaConnected = metaConnection?.status === "connected";

  const loadPhoneNumbers = useCallback(
    async (options?: { silent?: boolean; skipConnectionCheck?: boolean }) => {
      const { silent = false, skipConnectionCheck = false } = options ?? {};
      if (!clinicId) {
        if (!silent) {
          toast.error(t("whatsappMetaSelectClinicToListNumbers"));
        }
        return;
      }
      if (!skipConnectionCheck && !isMetaConnected) {
        toast.error(t("whatsappMetaConnectionStatusNotConnected"));
        return;
      }
      if (!wabaId.trim()) {
        if (!silent) {
          toast.error(t("whatsappMetaSelectWabaToListNumbers"));
        }
        return;
      }
      setIsLoadingNumbers(true);
      try {
        const { data, error } = await supabase.functions.invoke("meta-phone-numbers-list", {
          body: { clinicId, wabaId: wabaId.trim() },
        });
        const result = data as { success?: boolean; error?: string; phoneNumbers?: LiveMetaPhoneNumber[] } | null;
        if (error || !result?.success) {
          toast.error(error?.message || result?.error || t("whatsappMetaListNumbersError"));
          return;
        }
        setLivePhoneNumbers(Array.isArray(result?.phoneNumbers) ? result.phoneNumbers : []);
        if (!silent) {
          toast.success(t("whatsappMetaNumbersUpdated"));
        }
      } finally {
        setIsLoadingNumbers(false);
      }
    },
    [clinicId, isMetaConnected, t, wabaId]
  );

  const selectPhoneNumber = async (pickedPhoneNumberId: string) => {
    if (!clinicId) return;
    if (!isMetaConnected) return;
    if (!wabaId.trim()) return;
    setIsSelectingNumber(pickedPhoneNumberId);
    try {
      const { data, error } = await supabase.functions.invoke("meta-phone-number-select", {
        body: { clinicId, wabaId: wabaId.trim(), phoneNumberId: pickedPhoneNumberId, enable: true },
      });
      const result = data as { success?: boolean; error?: string } | null;
      if (error || !result?.success) {
        toast.error(error?.message || result?.error || t("whatsappMetaSelectNumberError"));
        return;
      }
      setPhoneNumberId(pickedPhoneNumberId);
      toast.success(t("whatsappMetaNumberSelected"));
    } finally {
      setIsSelectingNumber(null);
    }
  };

  const selectedTemplate = useMemo(
    () => availableTemplates.find((template) => template.name === templateName) ?? null,
    [availableTemplates, templateName]
  );

  const formattedNextReminderTrigger = useMemo(() => {
    if (!nextReminderTrigger?.next_trigger_at) return t("whatsappMetaReminderNextTriggerEmpty");
    const triggerDate = new Date(nextReminderTrigger.next_trigger_at);
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(triggerDate);
  }, [nextReminderTrigger, t]);

  const reminderTemplateWarning = useMemo(() => {
    if (!isEnabled) return t("whatsappMetaReminderWarnCloudDisabled");
    if (!templateName.trim()) return t("whatsappMetaReminderWarnTemplateMissing");
    if (selectedTemplate && selectedTemplate.status !== "APPROVED") {
      return t("whatsappMetaReminderWarnTemplateNotApproved");
    }
    return null;
  }, [isEnabled, selectedTemplate, t, templateName]);

  const loadTemplates = useCallback(async () => {
    if (!clinicId) {
      toast.error(t("whatsappMetaSelectClinicToLoadTemplates"));
      return;
    }
    if (!isMetaConnected) {
      toast.error(t("whatsappMetaConnectionStatusNotConnected"));
      return;
    }

    setIsLoadingTemplates(true);
    try {
      const { data, error } = await supabase.functions.invoke("list-whatsapp-templates", {
        body: { clinicId },
      });
      const result = data as { success?: boolean; templates?: unknown[]; error?: string } | null;

      if (error || !result?.success) {
        toast.error(error?.message || result?.error || t("whatsappMetaLoadTemplatesError"));
        return;
      }

      const templates = Array.isArray(result.templates) ? (result.templates as MetaTemplate[]) : [];
      setAvailableTemplates(templates);
      toast.success(t("whatsappMetaTemplatesLoaded", { count: templates.length }));
    } finally {
      setIsLoadingTemplates(false);
    }
  }, [clinicId, isMetaConnected, t]);

  // Carregar templates ao iniciar a tela quando houver clinicId
  useEffect(() => {
    if (clinicId && isMetaConnected) loadTemplates();
  }, [clinicId, isMetaConnected, loadTemplates]);

  const insertReminderToken = (token: ApplicationReminderTokenKey) => {
    setNewTemplateBody((current) => {
      const prefix = current.trim().length > 0 && !current.endsWith(" ") ? " " : "";
      return `${current}${prefix}{${token}}`;
    });
  };

  const saveCloudConfig = async (options?: {
    templateNameOverride?: string;
    templateLanguageOverride?: string;
    templateVariableMappingOverride?: Record<string, string>;
    successMessage?: string;
  }) => {
    if (!clinicId) {
      toast.error(t("whatsappMetaSelectClinicToSaveConfig"));
      return false;
    }
    if (!isMetaConnected) {
      toast.error(t("whatsappMetaConnectionStatusNotConnected"));
      return false;
    }

    try {
      await upsertCloudConfig.mutateAsync({
        clinicId,
        config: {
          isEnabled,
          phoneNumberId: phoneNumberId.trim(),
          templateName: options?.templateNameOverride ?? (templateName.trim() || null),
          templateLanguage: options?.templateLanguageOverride ?? templateLanguage,
          recipientStrategy: "phone_first",
          sendMode: "template",
          templateVariableMapping: options?.templateVariableMappingOverride ?? templateVariableMapping,
        },
      });
      setIsTemplateSelectionDirty(false);
      toast.success(options?.successMessage ?? t("whatsappMetaConfigSaved"));
      return true;
    } catch {
      toast.error(t("whatsappMetaConfigSaveError"));
      return false;
    }
  };

  useEffect(() => {
    if (!clinicId) return;
    if (!wabaId.trim()) return;
    if (!isMetaConnected) return;
    void loadPhoneNumbers({ silent: true });
  }, [clinicId, isMetaConnected, wabaId, loadPhoneNumbers]);

  const saveWebhook = async () => {
    if (!clinicId) {
      toast.error(t("whatsappMetaSelectClinicToSaveWebhook"));
      return;
    }
    if (!isMetaConnected) {
      toast.error(t("whatsappMetaConnectionStatusNotConnected"));
      return;
    }
    try {
      const callbackUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/whatsapp-webhook`;
      await upsertWebhookSub.mutateAsync({
        clinicId,
        subscription: {
          wabaId: wabaId.trim() || null,
          callbackUrl,
          verifyTokenName: FIXED_WEBHOOK_VERIFY_TOKEN_SECRET,
          appSecretName: FIXED_WEBHOOK_APP_SECRET,
          isActive: webhookIsActive,
        },
      });
      toast.success(t("whatsappMetaWebhookSaved"));
    } catch {
      toast.error(t("whatsappMetaWebhookSaveError"));
    }
  };

  const saveApplicationReminderRule = async () => {
    if (!clinicId) {
      toast.error(t("whatsappMetaSelectClinicToSaveConfig"));
      return;
    }
    if (!isMetaConnected) {
      toast.error(t("whatsappMetaConnectionStatusNotConnected"));
      return;
    }

    const numericOffset = Number(reminderOffsetValue);
    if (!Number.isInteger(numericOffset) || numericOffset <= 0 || numericOffset > 365) {
      toast.error(t("whatsappMetaReminderOffsetValueError"));
      return;
    }

    try {
      await upsertApplicationReminderRule.mutateAsync({
        clinicId,
        rule: {
          isActive: reminderIsActive,
          offsetUnit: reminderOffsetUnit,
          offsetValue: numericOffset,
          sendTime: `${reminderSendTime}:00`,
          timezone: reminderTimezone.trim() || "America/Sao_Paulo",
        },
      });
      toast.success(t("whatsappMetaReminderSaveSuccess"));
    } catch {
      toast.error(t("whatsappMetaReminderSaveError"));
    }
  };

  const sendTest = async () => {
    if (!clinicId) {
      toast.error(t("whatsappMetaSelectClinicToSendTest"));
      return;
    }
    if (!isMetaConnected) {
      toast.error(t("whatsappMetaConnectionStatusNotConnected"));
      return;
    }
    if (!testPhone.trim()) {
      toast.error(t("whatsappMetaTestPhoneRequired"));
      return;
    }

    setIsSendingTest(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-whatsapp-test-message", {
        body: { clinicId, toPhone: testPhone.trim() },
      });

      const result = data as { success?: boolean; error?: string; wamid?: string | null } | null;
      if (error || !result?.success) {
        toast.error(error?.message || result?.error || t("whatsappMetaSendTestError"));
        return;
      }
      toast.success(
        t("whatsappMetaSendTestSuccess", {
          wamid: result.wamid ? ` (wamid ${result.wamid})` : "",
        })
      );
    } finally {
      setIsSendingTest(false);
    }
  };

  const createTemplate = async () => {
    if (!clinicId) {
      toast.error(t("whatsappMetaSelectClinicToCreateTemplate"));
      return;
    }
    if (!isMetaConnected) {
      toast.error(t("whatsappMetaConnectionStatusNotConnected"));
      return;
    }
    if (!wabaId.trim()) {
      toast.error(t("whatsappMetaBusinessAccountIdRequired"));
      return;
    }
    if (!newTemplateName.trim()) {
      toast.error(t("whatsappMetaTemplateNameRequired"));
      return;
    }
    if (!newTemplateBody.trim()) {
      toast.error(t("whatsappMetaTemplateBodyRequired"));
      return;
    }
    if (unknownTemplateTokens.length > 0 || !reminderMetaTemplate) {
      toast.error(t("whatsappMetaTemplateUnknownTokens", { tokens: unknownTemplateTokens.join(", ") }));
      return;
    }

    setIsCreatingTemplate(true);
    try {
      const headerPlaceholderCount = countUniquePlaceholderSlots(newTemplateHeader);

      const headerExampleValues = newTemplateHeaderExamples
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);

      if (headerPlaceholderCount > 0 && headerExampleValues.length !== headerPlaceholderCount) {
        toast.error(t("whatsappMetaHeaderExamplesCountError", { count: headerPlaceholderCount }));
        return;
      }

      const quickReplyButtons = newTemplateQuickReplies
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
        .slice(0, 3);

      const { data, error } = await supabase.functions.invoke("create-whatsapp-template", {
        body: {
          clinicId,
          wabaId: wabaId.trim(),
          name: newTemplateName.trim(),
          category: newTemplateCategory,
          language: newTemplateLanguage,
          headerText: newTemplateHeader.trim() || null,
          headerExampleValues,
          bodyText: reminderMetaTemplate.text,
          bodyExampleValues: reminderMetaTemplate.examples,
          footerText: newTemplateFooter.trim() || null,
          quickReplyButtons,
        },
      });

      const result = data as { success?: boolean; error?: string } | null;
      if (error || !result?.success) {
        toast.error(error?.message || result?.error || t("whatsappMetaCreateTemplateError"));
        return;
      }

      setTemplateName(newTemplateName.trim());
      setTemplateLanguage(newTemplateLanguage);
      setTemplateVariableMapping(reminderMetaTemplate.mapping);
      await saveCloudConfig({
        templateNameOverride: newTemplateName.trim(),
        templateLanguageOverride: newTemplateLanguage,
        templateVariableMappingOverride: reminderMetaTemplate.mapping,
        successMessage: t("whatsappMetaTemplateSavedWithMapping"),
      });
      toast.success(t("whatsappMetaCreateTemplateSuccess"));
      setNewTemplateHeader("");
      setNewTemplateBody(APPLICATION_REMINDER_TEMPLATE_DEFAULT_BODY);
      setNewTemplateHeaderExamples("");
      setNewTemplateFooter("");
      setNewTemplateQuickReplies("");
      await loadTemplates();
    } finally {
      setIsCreatingTemplate(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("whatsappMetaTitle")}</CardTitle>
          <CardDescription>
            {t("whatsappMetaSubtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            <div className="rounded-md border p-3 space-y-3">
              <div className="font-medium text-sm">{t("whatsappMetaBasicConfigTitle")}</div>
              <div className="rounded-md border bg-muted/30 p-3 space-y-3">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div className="space-y-1">
                    <div className="text-sm font-medium">{t("whatsappMetaEmbeddedSignupTitle")}</div>
                  <div className="text-xs text-muted-foreground">
                    {t("whatsappMetaEmbeddedSignupDescription")}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge variant={isMetaConnected ? "default" : "secondary"}>
                    {isMetaConnected ? t("whatsappMetaConnectionStatusConnected") : t("whatsappMetaConnectionStatusNotConnected")}
                  </Badge>
                  {isMetaConnected && metaConnection?.waba_id ? <Badge variant="outline">WABA {metaConnection.waba_id}</Badge> : null}
                  {isMetaConnected && metaConnection?.meta_business_id ? (
                    <Badge variant="outline">Business {metaConnection.meta_business_id}</Badge>
                  ) : null}
                </div>
              </div>
              <div className="flex flex-col gap-3 md:flex-row md:items-center">
                  <Button
                    type="button"
                  onClick={isMetaConnected ? () => setIsDisconnectHelpOpen(true) : startEmbeddedSignup}
                  disabled={
                    !isEnabled ||
                    (!clinicId || (!isMetaConnected && (isEmbeddedSignupLoading || !isMetaSdkReady)))
                  }
                >
                  {isMetaConnected ? null : isEmbeddedSignupLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {isMetaConnected ? t("whatsappMetaDisconnectMeta") : t("whatsappMetaConnectButton")}
                </Button>
                <div className="text-xs text-muted-foreground">
                  {!META_APP_ID || !META_CONFIG_ID
                    ? t("whatsappMetaMissingEmbeddedConfig")
                    : isMetaSdkReady
                      ? t("whatsappMetaSdkReady")
                      : t("whatsappMetaSdkLoading")}
                </div>
              </div>
              {embeddedSignupPayload ? (
                <div className="text-xs text-muted-foreground break-all">
                  {t("whatsappMetaLatestEmbeddedEvent", { payload: JSON.stringify(embeddedSignupPayload) })}
                </div>
              ) : null}
            </div>
          </div>

          <div className="rounded-md border bg-muted/30 p-3 space-y-3">
            <div className="font-medium text-sm">{t("whatsappMetaSelectNumberTitle")}</div>
            <p className="text-xs text-muted-foreground">{t("whatsappMetaSelectNumberHint")}</p>
            {livePhoneNumbers.length > 0 ? (
              <RadioGroup
                value={phoneNumberId}
                onValueChange={(value) => {
                  if (value) {
                    void selectPhoneNumber(value);
                  }
                }}
                className="space-y-2"
              >
                {livePhoneNumbers.map((n) => {
                  if (!n.id) return null;
                  const qualityValue = qualityLabel(n.quality_rating);
                  return (
                    <label
                      key={n.id}
                      htmlFor={`whatsapp-phone-${n.id}`}
                      className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 rounded-md border p-2"
                    >
                      <div className="flex items-center gap-2 md:grow">
                        <RadioGroupItem
                          value={n.id}
                          id={`whatsapp-phone-${n.id}`}
                          disabled={!clinicId || !isMetaConnected || isSelectingNumber === n.id}
                        />
                        <div className="text-sm">
                          <div className="font-medium">
                            {n.display_phone_number ?? n.id}
                            {phoneNumberId === n.id ? (
                              <Badge className="ml-2" variant="secondary">
                                {t("whatsappMetaActiveBadge")}
                              </Badge>
                            ) : null}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {t("whatsappMetaNumberQualityStatus", { quality: qualityValue })}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground flex flex-wrap gap-3">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center gap-1 cursor-help">
                                <span>{t("whatsappMetaNumberQualityLabel")}</span>
                                <CircleHelp className="h-3 w-3" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-sm space-y-3">
                              <div className="space-y-1">
                                <p className="font-medium">{t("whatsappMetaQualityTooltipTitle")}</p>
                                <p>{t("whatsappMetaQualityTooltipIntro")}</p>
                              </div>
                              <div className="space-y-1">
                                <p className="font-medium">{t("whatsappMetaQualityTooltipSignalsTitle")}</p>
                                <ul className="list-disc pl-4">
                                  <li>{t("whatsappMetaQualityTooltipSignalBlocked")}</li>
                                  <li>{t("whatsappMetaQualityTooltipSignalSpam")}</li>
                                  <li>{t("whatsappMetaQualityTooltipSignalStopsInteracting")}</li>
                                  <li>{t("whatsappMetaQualityTooltipSignalEngagement")}</li>
                                </ul>
                              </div>
                              <div className="space-y-1">
                                <p className="font-medium">{t("whatsappMetaQualityTooltipLevelsTitle")}</p>
                                <ul className="list-disc pl-4">
                                  <li>{t("whatsappMetaQualityTooltipLevelGreen")}</li>
                                  <li>{t("whatsappMetaQualityTooltipLevelYellow")}</li>
                                  <li>{t("whatsappMetaQualityTooltipLevelRed")}</li>
                                </ul>
                              </div>
                              <div className="space-y-1">
                                <p className="font-medium">{t("whatsappMetaQualityTooltipConsequencesTitle")}</p>
                                <p>{qualityHelp(n.quality_rating)}</p>
                                <ul className="list-disc pl-4">
                                  <li>{t("whatsappMetaQualityTooltipConsequenceLimit")}</li>
                                  <li>{t("whatsappMetaQualityTooltipConsequenceRestrict")}</li>
                                  <li>{t("whatsappMetaQualityTooltipConsequenceBan")}</li>
                                </ul>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </label>
                  );
                })}
              </RadioGroup>
            ) : (
              <div className="text-xs text-muted-foreground">{t("whatsappMetaNoEnabledNumbersHint")}</div>
            )}
          </div>

          <div className="rounded-md border p-3 space-y-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2 font-medium text-sm">
                <MessageSquareText className="h-4 w-4" />
                {t("whatsappMetaCreateTemplateTitle")}
              </div>
              <p className="text-xs text-muted-foreground">{t("whatsappMetaCreateTemplateDescription")}</p>
            </div>
            <div className="grid gap-3 md:grid-cols-4">
              <div className="space-y-2 md:col-span-2">
                <Label>{t("whatsappMetaNewTemplateNameLabel")}</Label>
                <Input
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  placeholder="vacina_lembrete_1"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("whatsappMetaNewTemplateCategoryLabel")}</Label>
                <Select
                  value={newTemplateCategory}
                  onValueChange={(value) =>
                    setNewTemplateCategory(value as "MARKETING" | "UTILITY" | "AUTHENTICATION")
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UTILITY">{t("whatsappMetaCategoryUtility")}</SelectItem>
                    <SelectItem value="MARKETING">{t("whatsappMetaCategoryMarketing")}</SelectItem>
                    <SelectItem value="AUTHENTICATION">{t("whatsappMetaCategoryAuthentication")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("whatsappMetaTemplateLangLabel")}</Label>
                <Select
                  value={newTemplateLanguage}
                  onValueChange={setNewTemplateLanguage}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pt_BR">Português Brasil (pt_BR)</SelectItem>
                    <SelectItem value="pt_PT">Português Portugal (pt_PT)</SelectItem>
                    <SelectItem value="en">English (en)</SelectItem>
                    <SelectItem value="es">Español (es)</SelectItem>
                    <SelectItem value="es_AR">Español Argentina (es_AR)</SelectItem>
                    <SelectItem value="es_ES">Español España (es_ES)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2 md:col-span-2">
                <Label>{t("whatsappMetaNewTemplateHeaderLabel")}</Label>
                <Input
                  value={newTemplateHeader}
                  onChange={(e) => setNewTemplateHeader(e.target.value)}
                  placeholder={t("whatsappMetaNewTemplateHeaderPlaceholder")}
                />
              </div>
            </div>
            <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_320px]">
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label>{t("whatsappMetaNewTemplateBodyLabel")}</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setNewTemplateBody(APPLICATION_REMINDER_TEMPLATE_DEFAULT_BODY)}
                  >
                    {t("whatsappMetaTemplateUseDefault")}
                  </Button>
                </div>
                <Textarea
                  value={newTemplateBody}
                  onChange={(e) => setNewTemplateBody(e.target.value)}
                  placeholder={t("whatsappMetaNewTemplateBodyPlaceholder")}
                  className="min-h-32"
                />
                {unknownTemplateTokens.length > 0 ? (
                  <p className="text-xs text-destructive">
                    {t("whatsappMetaTemplateUnknownTokens", { tokens: unknownTemplateTokens.join(", ") })}
                  </p>
                ) : null}
              </div>
              <div className="rounded-md bg-muted/40 p-3 space-y-2">
                <div>
                  <div className="text-sm font-medium">{t("whatsappMetaTemplateLabelsTitle")}</div>
                  <p className="text-xs text-muted-foreground">{t("whatsappMetaTemplateLabelsDescription")}</p>
                </div>
                <Accordion type="multiple" className="w-full space-y-1">
                  {Object.entries(reminderTokenGroups).map(([category, tokens]) => (
                    tokens.length > 0 ? (
                      <AccordionItem
                        key={category}
                        value={category}
                        className="border rounded-md bg-background px-3"
                      >
                        <AccordionTrigger className="py-2 text-sm font-medium hover:no-underline">
                          {tokenCategoryLabels[category as ApplicationReminderTokenCategory]}
                        </AccordionTrigger>
                        <AccordionContent className="pb-2">
                          <div className="flex flex-wrap gap-2">
                            {tokens.map((token) => (
                              <Button
                                key={token.key}
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => insertReminderToken(token.key)}
                                title={token.mapping}
                              >
                                {`{${token.key}}`}
                              </Button>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ) : null
                  ))}
                </Accordion>
              </div>
            </div>
            <div className="grid gap-3 lg:grid-cols-2">
              <div className="rounded-md border bg-background p-3 space-y-2">
                <div className="text-sm font-medium">{t("whatsappMetaTemplatePreviewTitle")}</div>
                <p className="text-xs text-muted-foreground">{t("whatsappMetaTemplatePreviewDescription")}</p>
                <p className="rounded-md bg-muted/50 p-3 text-sm leading-relaxed">{reminderTemplatePreview}</p>
              </div>
              <div className="rounded-md border bg-background p-3 space-y-2">
                <div className="text-sm font-medium">{t("whatsappMetaTemplateMetaPreviewTitle")}</div>
                <p className="rounded-md bg-muted/50 p-3 font-mono text-xs leading-relaxed">
                  {reminderMetaTemplate?.text ?? newTemplateBody}
                </p>
                {reminderMetaTemplate ? (
                  <div className="space-y-1">
                    <div className="text-xs font-medium text-muted-foreground">
                      {t("whatsappMetaTemplateMappingTitle")}
                    </div>
                    {Object.entries(reminderMetaTemplate.mapping).map(([position, mapping]) => (
                      <div key={position} className="flex items-center justify-between gap-2 text-xs">
                        <Badge variant="outline">{`{{${position}}}`}</Badge>
                        <span className="text-muted-foreground">{mapping}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="flex justify-end">
              <Button type="button" onClick={createTemplate} disabled={isCreatingTemplate || !clinicId || !isMetaConnected}>
                {isCreatingTemplate ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t("whatsappMetaCreateTemplateButton")}
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t("whatsappMetaTemplateNameLabel")}</Label>
            <div className="flex flex-wrap gap-2 items-center">
              <Button type="button" variant="outline" onClick={loadTemplates} disabled={isLoadingTemplates || !clinicId || !isMetaConnected}>
                {isLoadingTemplates ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                {t("whatsappMetaRefreshTemplates")}
              </Button>
              <Badge variant="secondary">{t("whatsappMetaTemplatesFound", { count: availableTemplates.length })}</Badge>
              {selectedTemplate && selectedTemplate.status !== "APPROVED" && (
                <Badge variant="destructive">
                  {t("whatsappMetaTemplateNotApproved", { status: templateStatusLabel(selectedTemplate.status) })}
                </Badge>
              )}
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              {availableTemplates.length > 0 ? (
                <Select
                  value={templateName}
                  onValueChange={(value) => {
                    setTemplateName(value);
                    const found = availableTemplates.find((t) => t.name === value);
                    if (found?.language) setTemplateLanguage(found.language);
                    setIsTemplateSelectionDirty(true);
                  }}
                >
                  <SelectTrigger className={`flex-1 min-w-[200px] ${selectedTemplate && selectedTemplate.status !== "APPROVED" ? "border-destructive" : ""}`}>
                    <SelectValue placeholder={t("whatsappMetaSelectTemplatePlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTemplates.map((template) => {
                      const approved = template.status === "APPROVED";
                      const statusLabel = templateStatusLabel(template.status);
                      return (
                        <SelectItem
                          key={`${template.name}:${template.language ?? ""}`}
                          value={template.name}
                          disabled={!approved}
                        >
                          <span className="flex items-center gap-2">
                            {template.name}
                            {template.language && (
                              <Badge variant="outline" className="text-xs">{template.language}</Badge>
                            )}
                            <Badge
                              variant={approved ? "default" : template.status === "REJECTED" ? "destructive" : "secondary"}
                              className="text-xs"
                            >
                              {statusLabel}
                            </Badge>
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  className="flex-1 min-w-[200px]"
                  value={templateName}
                  onChange={(e) => {
                    setTemplateName(e.target.value);
                    setIsTemplateSelectionDirty(true);
                  }}
                  placeholder="vaccine_due_reminder"
                />
              )}
              <Button
                type="button"
                variant={isTemplateSelectionDirty ? "default" : "outline"}
                onClick={() => void saveCloudConfig()}
                disabled={!clinicId || !isMetaConnected || upsertCloudConfig.isPending || !templateName.trim()}
              >
                {upsertCloudConfig.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t("whatsappMetaSaveConfigTemplate")}
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <div className="space-y-1 flex-1 min-w-[160px] max-w-[240px]">
                <Label className="text-xs text-muted-foreground">{t("whatsappMetaTemplateLangLabel")}</Label>
                <Select
                  value={templateLanguage}
                  onValueChange={(value) => {
                    setTemplateLanguage(value);
                    setIsTemplateSelectionDirty(true);
                  }}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pt_BR">Português Brasil (pt_BR)</SelectItem>
                    <SelectItem value="pt_PT">Português Portugal (pt_PT)</SelectItem>
                    <SelectItem value="en">English (en)</SelectItem>
                    <SelectItem value="es">Español (es)</SelectItem>
                    <SelectItem value="es_AR">Español Argentina (es_AR)</SelectItem>
                    <SelectItem value="es_ES">Español España (es_ES)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {isTemplateSelectionDirty ? (
              <p className="text-xs text-muted-foreground">{t("whatsappMetaTemplateDirtyHint")}</p>
            ) : null}
          </div>

          <p className="text-xs text-muted-foreground">
            {t("whatsappMetaAvailableEntitiesHint")}
          </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("whatsappMetaReminderTitle")}</CardTitle>
          <CardDescription>{t("whatsappMetaReminderSubtitle")}</CardDescription>
        </CardHeader>
        <CardContent className={`space-y-4 ${isEnabled ? "" : "opacity-50 pointer-events-none"}`}>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="flex items-center gap-2 md:mt-8">
              <Checkbox
                id="whatsapp-vaccine-reminder-active"
                checked={reminderIsActive}
                onCheckedChange={(value) => setReminderIsActive(value === true)}
              />
              <Label htmlFor="whatsapp-vaccine-reminder-active">{t("whatsappMetaReminderEnabled")}</Label>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-2">
              <Label>{t("whatsappMetaReminderOffsetUnitLabel")}</Label>
              <Select
                value={reminderOffsetUnit}
                onValueChange={(value) => setReminderOffsetUnit(value as "days" | "weeks" | "months")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weeks">{t("whatsappMetaReminderOffsetUnitWeeks")}</SelectItem>
                  <SelectItem value="months">{t("whatsappMetaReminderOffsetUnitMonths")}</SelectItem>
                  <SelectItem value="days">{t("whatsappMetaReminderOffsetUnitDays")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("whatsappMetaReminderOffsetValueLabel")}</Label>
              <Input
                type="number"
                min={1}
                max={365}
                value={reminderOffsetValue}
                onChange={(e) => setReminderOffsetValue(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t("whatsappMetaReminderTimeLabel")}</Label>
            <Input
              type="time"
              value={reminderSendTime}
              onChange={(e) => setReminderSendTime(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>{t("whatsappMetaReminderTimezoneLabel")}</Label>
            <Input
              value={reminderTimezone}
              onChange={(e) => setReminderTimezone(e.target.value)}
              placeholder="America/Sao_Paulo"
            />
          </div>

          <div className="rounded-md border p-3 space-y-2">
            <p className="text-sm">
              <span className="font-medium">{t("whatsappMetaReminderTemplateSelectedLabel")}:</span>{" "}
              {templateName?.trim()
                ? `${templateName} (${templateLanguage})`
                : t("whatsappMetaReminderTemplateNotConfigured")}
            </p>
            <p className="text-sm">
              <span className="font-medium">{t("whatsappMetaReminderNextTriggerLabel")}:</span>{" "}
              {isLoadingNextReminderTrigger ? t("whatsappMetaReminderNextTriggerLoading") : formattedNextReminderTrigger}
            </p>
            {reminderTemplateWarning ? (
              <p className="text-xs text-amber-600">{reminderTemplateWarning}</p>
            ) : (
              <p className="text-xs text-muted-foreground">{t("whatsappMetaReminderHealthyConfig")}</p>
            )}
          </div>

          <div className="flex justify-end">
            <Button
                type="button"
                onClick={saveApplicationReminderRule}
                disabled={!isEnabled || !clinicId || !isMetaConnected || upsertApplicationReminderRule.isPending}
              >
              {upsertApplicationReminderRule.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t("whatsappMetaReminderSaveButton")}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("whatsappMetaTestTitle")}</CardTitle>
        </CardHeader>
        <CardContent className={`space-y-3 ${isEnabled ? "" : "opacity-50 pointer-events-none"}`}>
          <div className="space-y-2">
            <Label>{t("whatsappMetaTestPhoneLabel")}</Label>
            <Input
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="+5511999999999"
            />
            <p className="text-xs text-muted-foreground">
              {t("whatsappMetaSendTestExtraHint")}
            </p>
          </div>
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={sendTest}
              disabled={
                !isEnabled ||
                isSendingTest ||
                !clinicId ||
                !testPhone ||
                !templateName.trim() ||
                isTemplateSelectionDirty ||
                upsertCloudConfig.isPending ||
                (!!selectedTemplate && selectedTemplate.status !== "APPROVED") ||
                !isMetaConnected
              }
            >
              {isSendingTest ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t("whatsappMetaSendTestButton")}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isDisconnectHelpOpen} onOpenChange={setIsDisconnectHelpOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{t("whatsappMetaDisconnectMeta")}</DialogTitle>
            <DialogDescription className="space-y-3 pt-2">
              <p>{t("whatsappMetaDisconnectHelpIntro")}</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>{t("whatsappMetaDisconnectHelpStep1")}</li>
                <li>{t("whatsappMetaDisconnectHelpStep2")}</li>
                <li>{t("whatsappMetaDisconnectHelpStep3")}</li>
              </ul>
              <p className="text-muted-foreground text-sm">
                {t("whatsappMetaDisconnectHelpFooter")}
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setIsDisconnectHelpOpen(false)}>
              {t("whatsappMetaDisconnectHelpOk")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
